/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getCheckoutState, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
//import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    AttributeValue,
    CartLine,
    Customer,
    //ChannelDeliveryOptionConfiguration,
    //DeliveryOption,
    OrgUnitLocation,
    ProductDeliveryOptions,
    ProductWarehouseInventoryInformation,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { IStoreSelectorStateManager } from '@msdyn365-commerce-modules/bopis-utilities';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IAny, ICoreContext, IGeneric, RichTextComponent } from '@msdyn365-commerce/core';
import { getDelayedDeliveryMsg, getDeliveryDateRange } from '../../../themes/dobbies/utilities/estDeliveryDate';
import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { mapProductInventoryInformation } from '../../../themes/dobbies/utilities';

export interface IPickUpInStoreViewProps {
    callbacks: {
        toggleBopis(isBopisSelected: boolean): void;
    };
    defaultComponent?: React.ReactNode;
    orgUnitName?: string;
    isBopisSelected: boolean;
    ContainerProps: INodeProps;
    deliveryOption?: string;
}

export interface IPickUpInStoreProps {
    cartline: CartLine;
    product: SimpleProduct;
    shipitText: string;
    pickUpInStoreText: string;
    changeStoreText: string;
    cartState: ICartState | undefined;
    storeSelectorStateManager: IStoreSelectorStateManager | undefined;
    orgUnitLocations?: OrgUnitLocation[] | undefined;
    deliveryOptions?: ProductDeliveryOptions;
    pickupDeliveryModeCode?: string;
    storeSelectorModuleId?: string;
    productAttributes?: AttributeValue[];
    //channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    //retailMulitplePickupFeatureState?: boolean;
    locationChangedHandler(): void;
    context: ICoreContext<IGeneric<IAny>>;
    customerInformation: Customer | undefined;
    productAvailabilities?: IProductInventoryInformation[];
    cncProductAvailabilities?: ProductWarehouseInventoryInformation;
    checkoutState?: ICheckoutState;
}

export const PickUpInStore = (props: IPickUpInStoreProps): IPickUpInStoreViewProps | undefined => {
    const _onLocationChangedCallback = async (
        orgUnitLocation: OrgUnitLocation,
        cartState: ICartState | undefined,
        cartline: CartLine,
        deliveryMode?: string
    ) => {
        if (!cartState) {
            return Promise.resolve();
        }
        return cartState
            .updateCartLinePickupLocation({ cartLineId: cartline.LineId!, location: orgUnitLocation, deliveryMode })
            .then(result => {
                if (result.status === 'SUCCESS') {
                    props.locationChangedHandler();
                }
            })
            .catch(error => {});
    };

    const productAvailabilitesCNC = mapProductInventoryInformation(
        props.context.actionContext,
        props.cncProductAvailabilities?.ProductWarehouseInventoryAvailabilities?.filter(
            warehouseAvailability => warehouseAvailability.ProductId === props.cartline.ProductId
        ) || []
    );
    const isCnCAvailable = productAvailabilitesCNC.find(pa => pa.IsProductAvailable);
    const onlyInStoreVal = props.productAttributes?.find(a => a.Name?.toLowerCase() === 'onlyinstore');
    const isOnlyInStore = onlyInStoreVal?.TextValue?.toLowerCase() === 'yes';
    const _toggleBOPIS = (isBopisSelected: boolean) => {
        const { storeSelectorStateManager, product, cartline, cartState, storeSelectorModuleId } = props;

        if (isBopisSelected) {
            if (!storeSelectorStateManager) {
                return;
            }
            storeSelectorStateManager
                .openDialog({
                    id: storeSelectorModuleId,
                    product,
                    alreadySelectedLocation: {
                        OrgUnitNumber: cartline.FulfillmentStoreId
                    },
                    deliveryOptions: props.deliveryOptions,
                    onLocationSelected: async (orgUnitLocation, deliveryMode: string | undefined) => {
                        return _onLocationChangedCallback(orgUnitLocation, cartState, cartline, deliveryMode);
                    }
                })
                .catch(error => {
                    console.log('Store selector manager error', error);
                });
        } else {
            if (!cartState) {
                return;
            }
            cartline.DeliveryMode = '';
            cartline.FulfillmentStoreId = '';
            cartline.ShippingAddress = {};
            cartState
                .clearCartLinePickupLocation({ cartLineId: cartline.LineId! })
                .then(async result => {
                    if (result.status === 'SUCCESS') {
                        const checkoutState = await getCheckoutState(props.context.actionContext);
                        await cartState.updateShippingAddress({ newShippingAddress: {} });
                        await checkoutState.checkoutCart.updateShippingAddress({ newShippingAddress: {} });
                        await cartState.refreshCart({});
                        props.locationChangedHandler();
                    }
                })
                .catch(error => {});
        }
    };

    const _getOrgUnitName = (fulfillmentStoreId: string | undefined, orgUnitLocations: OrgUnitLocation[] | undefined) => {
        if (!orgUnitLocations || !fulfillmentStoreId || orgUnitLocations.length === 0) {
            return '';
        }

        const foundLocation = orgUnitLocations.find(orgUnitLocation => {
            return orgUnitLocation.OrgUnitNumber === fulfillmentStoreId;
        });

        if (foundLocation) {
            return foundLocation.OrgUnitName;
        }
        return fulfillmentStoreId;
    };

    const isLowStock = (
        productId: number | undefined,
        productAvailabilities: any[],
        warehouseIdsForFulfillment: string[],
        isPlantCategory: boolean
    ): boolean => {
        const matchingInventories = productAvailabilities.filter(pa => pa.ProductAvailableQuantity?.ProductId === productId);
        if (isPlantCategory) {
            return matchingInventories.some(
                pa => warehouseIdsForFulfillment.includes(pa.InventLocationId?.toLowerCase()) && pa.StockLevelCode === 'LOW'
            );
        }

        return matchingInventories.some(pa => pa.StockLevelCode === 'LOW');
    };
    let isPlantDeliveryStockAvailable = false;
    const _renderShippingMethod = (
        cartLine: CartLine,
        shipItText: string,
        pickupInStoretext: string,
        changeStoreText: string,
        orgUnitLocations: OrgUnitLocation[] | undefined,
        deliveryOptions: ProductDeliveryOptions,
        context: ICoreContext<IGeneric<IAny>>
    ): JSX.Element => {
        const orgUnitName = _getOrgUnitName(cartLine.FulfillmentStoreId, orgUnitLocations);
        const bopisSelected = !!cartLine.FulfillmentStoreId;
        const toggleBOPIS = (isBopisSelected: boolean) => {
            return () => {
                _toggleBOPIS(isBopisSelected);
            };
        };

        const getShippingMethods = (isPlantDeliveryStockAvailable: boolean): JSX.Element => {
            const additionalMsgConfig = context.actionContext.requestContext.app.config.additionalDeliveryMessages;
            return (
                <>
                    {deliveryOptions.DeliveryOptions?.map(mode => {
                        const isShipping = mode.Code === 'STD';
                        const isSTD2 = mode.Code === 'STD-2';
                        const isPickup = mode.Code === 'ClickCol';
                        const isHD = mode.Code === 'HD';
                        const isPF = mode.Code === 'PF';
                        const isDTC05 = mode.Code === 'DTC-05days';
                        const isDTC10 = mode.Code === 'DTC-10days';
                        const isDTC14 = mode.Code === 'DTC-14days';
                        const isDTC28 = mode.Code === 'DTC-28days';
                        const isHDPresale = mode.Code === 'HD Presale';
                        const estDeliveryDate = getDeliveryDateRange(new Date(), props.context.actionContext, mode.Code || '');
                        const vendorNameAttr = props.productAttributes?.find(attr => {
                            return attr.Name?.toLowerCase() === 'vendorname';
                        });
                        const vendorName = vendorNameAttr && vendorNameAttr.TextValue;
                        const additionalMsgForDel = additionalMsgConfig?.find(
                            (delMsgForMode: { delMode: string; msg: string }) => delMsgForMode.delMode === mode.Code
                        )?.msg;
                        const delayedDelMsg = cartLine.ShippingAddress?.ZipCode
                            ? getDelayedDeliveryMsg(context.actionContext, addressPostcode || cartLine.ShippingAddress?.ZipCode)
                            : '';
                        if (isDTC05 || isDTC10 || isDTC14 || isDTC28) {
                            return (
                                <>
                                    {!isCnCAvailable ? <p className='only-hd'>Product is only available for home delivery.</p> : ''}
                                    <label key={`${cartLine.LineId}-shipit`} className='msc-cart-line__bopis-shipping'>
                                        <input
                                            className='delivery-radio'
                                            id={`ms-cart-bopis-ship-option-${cartLine.LineId}`}
                                            type='radio'
                                            name={`shippingType ${cartLine.LineId}`}
                                            onChange={toggleBOPIS(false)}
                                            value={shipItText}
                                            checked={!bopisSelected}
                                            aria-checked={!bopisSelected}
                                        />
                                        <span className='msc-cart-line__custom-radio'></span>
                                        <div className='msc-cart-line__delivery-option'>
                                            <div className='msc-cart-line__delivery-option__header'>
                                                <p>
                                                    {shipItText} <span> - dispatched by {vendorName ? vendorName : 'Dobbies'} </span>
                                                </p>
                                                <span className='msc-cart-line__delivery-option__header-icon'></span>
                                            </div>
                                            <div className='msc-cart-line__delivery-option__details'>
                                                <p>
                                                    Delivery: <span>{mode.Description}</span>
                                                </p>
                                                <p>
                                                    Estimated arrival by:{' '}
                                                    {estDeliveryDate?.maxDeliveryDate && (
                                                        <span className='msc-cart-line__delivery-date'>
                                                            {estDeliveryDate.maxDeliveryDate}
                                                        </span>
                                                    )}
                                                </p>
                                                <p>
                                                    {props.context.actionContext.requestContext.user.isAuthenticated && addressPostcode ? (
                                                        <>
                                                            Postcode: <span>{addressPostcode}</span>
                                                        </>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </p>
                                                <p className='delivery-note'>{` ${delayedDelMsg}`}</p>
                                            </div>
                                        </div>
                                    </label>
                                </>
                            );
                        }
                        if (isHD || isHDPresale) {
                            return (
                                <>
                                    <>
                                        {!isCnCAvailable ? <p className='only-hd'>Product is only available for home delivery.</p> : ''}
                                        <label key={`${cartLine.LineId}-shipit`} className='msc-cart-line__bopis-shipping'>
                                            <input
                                                className='delivery-radio'
                                                id={`ms-cart-bopis-ship-option-${cartLine.LineId}`}
                                                type='radio'
                                                name={`shippingType ${cartLine.LineId}`}
                                                onChange={toggleBOPIS(false)}
                                                value={shipItText}
                                                checked={!bopisSelected}
                                                aria-checked={!bopisSelected}
                                            />
                                            <span className='msc-cart-line__custom-radio'></span>
                                            <div className='msc-cart-line__delivery-option'>
                                                <div className='msc-cart-line__delivery-option__header'>
                                                    <p>
                                                        {shipItText} <span> - dispatched by {vendorName ? vendorName : 'Dobbies'} </span>
                                                    </p>
                                                    <span className='msc-cart-line__delivery-option__header-icon'></span>
                                                </div>
                                                <div className='msc-cart-line__delivery-option__details'>
                                                    <p>
                                                        Delivery: <span>{mode.Description}</span>
                                                    </p>
                                                    {additionalMsgForDel ? (
                                                        <p>
                                                            {/* {isPreSale ? (
                                                            <strong>We&apos;ll contact you to arrange delivery when stock arrives.</strong>
                                                        ) : (
                                                            <strong>You&apos;ll be contacted within 24 hours to select delivery date</strong>
                                                        )} */}
                                                            <span>
                                                                <RichTextComponent text={additionalMsgForDel} />
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <></>
                                                    )}

                                                    <p>
                                                        {props.context.actionContext.requestContext.user.isAuthenticated &&
                                                        addressPostcode ? (
                                                            <>
                                                                Postcode: <span>{addressPostcode}</span>
                                                            </>
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </label>
                                    </>
                                </>
                            );
                        }

                        if (isPF && isPlantDeliveryStockAvailable) {
                            return (
                                <>
                                    {!isCnCAvailable ? <p className='only-hd'>Product is only available for home delivery.</p> : ''}
                                    <label key={`${cartLine.LineId}-shipit`} className='msc-cart-line__bopis-shipping'>
                                        <input
                                            className='delivery-radio'
                                            id={`ms-cart-bopis-ship-option-${cartLine.LineId}`}
                                            type='radio'
                                            name={`shippingType ${cartLine.LineId}`}
                                            onChange={toggleBOPIS(false)}
                                            value={shipItText}
                                            checked={!bopisSelected}
                                            aria-checked={!bopisSelected}
                                        />
                                        <span className='msc-cart-line__custom-radio'></span>
                                        <div className='msc-cart-line__delivery-option'>
                                            <div className='msc-cart-line__delivery-option__header'>
                                                <p>
                                                    {shipItText} <span> - dispatched by {vendorName ? vendorName : 'Dobbies'} </span>
                                                </p>
                                                <span className='msc-cart-line__delivery-option__header-icon'></span>
                                            </div>
                                            <div className='msc-cart-line__delivery-option__details'>
                                                {mode?.Description && (
                                                    <p>
                                                        Delivery: <span>{mode.Description}</span>
                                                    </p>
                                                )}
                                                <p>
                                                    Estimated arrival by:{' '}
                                                    {estDeliveryDate?.maxDeliveryDate && (
                                                        <span className='msc-cart-line__delivery-date'>
                                                            {estDeliveryDate.maxDeliveryDate}
                                                        </span>
                                                    )}
                                                </p>
                                                <p>
                                                    {props.context.actionContext.requestContext.user.isAuthenticated && addressPostcode ? (
                                                        <>
                                                            Postcode: <span>{addressPostcode}</span>
                                                        </>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </p>
                                                <p className='delivery-note'>{` ${delayedDelMsg}`}</p>
                                            </div>

                                            {showLowStockWarning && !isPickup && !isCnCAvailable ? (
                                                <p className='delivery-note'>Hurry, just a few left - order soon!</p>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </label>
                                </>
                            );
                        }
                        if ((isShipping || isSTD2) && isHomeDelAvailable) {
                            return (
                                <>
                                    {!isCnCAvailable ? <p className='only-hd'>Product is only available for home delivery.</p> : ''}
                                    <label key={`${cartLine.LineId}-shipit`} className='msc-cart-line__bopis-shipping'>
                                        <input
                                            className='delivery-radio'
                                            id={`ms-cart-bopis-ship-option-${cartLine.LineId}`}
                                            type='radio'
                                            name={`shippingType ${cartLine.LineId}`}
                                            onChange={toggleBOPIS(false)}
                                            value={shipItText}
                                            checked={!bopisSelected}
                                            aria-checked={!bopisSelected}
                                        />
                                        <span className='msc-cart-line__custom-radio'></span>
                                        <div className='msc-cart-line__delivery-option'>
                                            <div className='msc-cart-line__delivery-option__header'>
                                                <p>
                                                    {shipItText} <span> - dispatched by {vendorName ? vendorName : 'Dobbies'} </span>
                                                </p>
                                                <span className='msc-cart-line__delivery-option__header-icon'></span>
                                            </div>
                                            <div className='msc-cart-line__delivery-option__details'>
                                                {mode?.Description && (
                                                    <p>
                                                        Delivery: <span>{mode.Description}</span>
                                                    </p>
                                                )}
                                                <p>
                                                    Estimated arrival by:{' '}
                                                    {estDeliveryDate?.maxDeliveryDate && (
                                                        <span className='msc-cart-line__delivery-date'>
                                                            {estDeliveryDate.maxDeliveryDate}
                                                        </span>
                                                    )}
                                                </p>
                                                <p>
                                                    {props.context.actionContext.requestContext.user.isAuthenticated && addressPostcode ? (
                                                        <>
                                                            Postcode: <span>{addressPostcode}</span>
                                                        </>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </p>
                                                <p className='delivery-note'>{` ${delayedDelMsg}`}</p>
                                            </div>

                                            {showLowStockWarning && !isPickup && !isCnCAvailable ? (
                                                <p className='delivery-note'>Hurry, just a few left - order soon!</p>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </label>
                                </>
                            );
                        }

                        if (isPickup && isCnCAvailable) {
                            const isPFItem = deliveryOptions.DeliveryOptions?.find(del => del.Code === 'PF');
                            return (
                                <>
                                    {!isHomeDelAvailable || (isPFItem && !isPlantDeliveryStockAvailable) ? (
                                        <p className='only-cnc'>Product is only available for click & collect.</p>
                                    ) : (
                                        ''
                                    )}
                                    <label key={`${cartLine.LineId}-pickup`} className='msc-cart-line__bopis-store-pickup'>
                                        <input
                                            className='delivery-radio'
                                            id={`ms-cart-bopis-pickup-option-${cartLine.LineId}`}
                                            type='radio'
                                            name={`shippingType ${cartLine.LineId}`}
                                            onChange={toggleBOPIS(true)}
                                            value={pickupInStoretext}
                                            checked={bopisSelected}
                                            aria-checked={bopisSelected}
                                        />
                                        <span className='msc-cart-line__custom-radio'></span>
                                        <div className='msc-cart-line__delivery-option'>
                                            <div className='msc-cart-line__delivery-option__header'>
                                                <p>{pickupInStoretext}</p>
                                                <span className='msc-cart-line__delivery-option__header-icon'></span>
                                            </div>
                                            <div className='msc-cart-line__delivery-option__details'>
                                                {cartLine.FulfillmentStoreId ? (
                                                    <div className='msc-cart-line__bopis__fullfilment'>
                                                        <p>In-stock: &nbsp;</p>
                                                        <span className='msc-cart-line__bopis__fullfilment-store'>
                                                            Dobbies {orgUnitName}.
                                                        </span>
                                                        <button
                                                            className='msc-cart-line__bopis-changestore btn'
                                                            onClick={toggleBOPIS(true)}
                                                        >
                                                            {changeStoreText}
                                                        </button>
                                                    </div>
                                                ) : null}
                                                {cartLine.FulfillmentStoreId ? (
                                                    ''
                                                ) : (
                                                    <a className='choose-store' onClick={toggleBOPIS(true)}>
                                                        Choose store
                                                    </a>
                                                )}
                                                <p>Ready within 24 hours if you order before 4pm.</p>
                                                <p>
                                                    We’ll email when your order is ready.{' '}
                                                    <a href={props.context.app.config.cartLineLearnMoreLink || '#'}>Learn more</a>
                                                </p>
                                                {showLowStockWarning ? (
                                                    <p className='delivery-note'>Hurry, just a few left - order soon!</p>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </div>
                                    </label>{' '}
                                </>
                            );
                        }
                        return <></>; // Skip "Common MOD" or unknown codes
                    })}
                </>
            );
        };

        let addressPostcode: string | undefined = '';
        if (props.context.actionContext.requestContext.user.isAuthenticated) {
            if (props.customerInformation?.Addresses && props.customerInformation?.Addresses.length) {
                let customerAddress;
                // If Shipping address name is empty (default address) then pick postcode from customer address
                if (props.cartState?.cart.ShippingAddress?.Name === '') {
                    customerAddress =
                        props.customerInformation?.Addresses.find(addr => addr.IsPrimary) || props.customerInformation?.Addresses[0];
                } else {
                    customerAddress = props.checkoutState?.checkoutCart.cart.ShippingAddress || props.cartState?.cart.ShippingAddress;
                }
                addressPostcode = customerAddress?.ZipCode;
            }
        }

        const fulfillmentAttributeName = props.context.actionContext.requestContext.app.config.fulfillmentAttributeName;
        const fulfillmentAttributeTextValue = props.context.actionContext.requestContext.app.config.fulfillmentAttributeTextValue;
        const attributeName = fulfillmentAttributeName
            ? fulfillmentAttributeName
                  .toString()
                  .toLowerCase()
                  .trim()
            : 'fulfillmenttype';
        const attributeTextValue = fulfillmentAttributeTextValue
            ? fulfillmentAttributeTextValue
                  .toString()
                  .toLowerCase()
                  .trim()
            : 'plants';
        const fulfillmentTypeAttribute =
            props.productAttributes &&
            props.productAttributes.find(attribute => {
                return attribute?.Name?.toLowerCase() === attributeName;
            });
        const isPlantFulfillment = fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
        const isPlantCategory = isPlantFulfillment ? true : false;
        //@ts-ignore
        let _homeDelStockLabel: string;
        //@ts-ignore
        let _cncStockLabel: string;
        if (isPlantCategory) {
            let warehouseIdsForFulfillment: string[] = props.context.actionContext.requestContext.app.config.warehouseIdsForFulfillment;
            warehouseIdsForFulfillment = warehouseIdsForFulfillment.map(element => element.toLowerCase());

            // Check if Plant home delivery stock is available in plants warehouses
            props.productAvailabilities?.map(productAvailability => {
                if (
                    productAvailability.InventLocationId &&
                    (warehouseIdsForFulfillment.includes(productAvailability.InventLocationId.toLowerCase()) ||
                        productAvailability.InventLocationId === 'ECOM') &&
                    productAvailability.ProductAvailableQuantity.ProductId === cartLine.ProductId
                ) {
                    _homeDelStockLabel = productAvailability?.StockLevelCode || '';
                    isPlantDeliveryStockAvailable = productAvailability.IsProductAvailable;
                }
            });
        } else {
            props.productAvailabilities?.map(productAvailability => {
                _homeDelStockLabel = productAvailability?.StockLevelCode || '';
            });
        }

        if (cartLine.FulfillmentStoreId) {
            props.cncProductAvailabilities?.ProductWarehouseInventoryAvailabilities?.map(cncAvailability => {
                if (
                    cncAvailability.InventLocationId?.toLowerCase() === cartLine.FulfillmentStoreId &&
                    cncAvailability.ProductId === cartLine.ProductId
                ) {
                    _cncStockLabel = cncAvailability.PhysicalAvailableInventoryLevelCode || '';
                }
            });
        }
        const productAvailabilitesCNC = mapProductInventoryInformation(
            props.context.actionContext,
            props.cncProductAvailabilities?.ProductWarehouseInventoryAvailabilities?.filter(
                warehouseAvailability => warehouseAvailability.ProductId === cartLine.ProductId
            ) || []
        );
        const isHomeDelAvailable = props.productAvailabilities?.find(
            pa => pa.IsProductAvailable && pa.ProductAvailableQuantity.ProductId === cartLine.ProductId && pa.InventLocationId === 'ECOM'
        );
        const isCnCAvailable = productAvailabilitesCNC.find(pa => pa.IsProductAvailable);

        const showLowStockWarning = isLowStock(
            cartLine.ProductId,
            props.productAvailabilities || [],
            props.context.actionContext.requestContext.app.config.warehouseIdsForFulfillment.map((x: string) => x.toLowerCase()),
            isPlantCategory
        );
        const shippingMethods = getShippingMethods(isPlantDeliveryStockAvailable);

        if (isOnlyInStore) {
            return (
                <>
                    <p className='only-cnc'>Product is only available for click & collect.</p>
                    <label key={`${cartLine.LineId}-pickup`} className='msc-cart-line__bopis-store-pickup'>
                        <input
                            className='delivery-radio'
                            id={`ms-cart-bopis-pickup-option-${cartLine.LineId}`}
                            type='radio'
                            name={`shippingType ${cartLine.LineId}`}
                            onChange={toggleBOPIS(true)}
                            value={pickupInStoretext}
                            checked={bopisSelected}
                            aria-checked={bopisSelected}
                        />
                        <span className='msc-cart-line__custom-radio'></span>
                        <div className='msc-cart-line__delivery-option'>
                            <div className='msc-cart-line__delivery-option__header'>
                                <p>{pickupInStoretext}</p>
                                <span className='msc-cart-line__delivery-option__header-icon'></span>
                            </div>
                            <div className='msc-cart-line__delivery-option__details'>
                                {cartLine.FulfillmentStoreId ? (
                                    <div className='msc-cart-line__bopis__fullfilment'>
                                        <p>In-stock: &nbsp;</p>
                                        <span className='msc-cart-line__bopis__fullfilment-store'>Dobbies {orgUnitName}.</span>
                                        <button className='msc-cart-line__bopis-changestore btn' onClick={toggleBOPIS(true)}>
                                            {changeStoreText}
                                        </button>
                                    </div>
                                ) : null}

                                <p>Ready within 24 hours if you order before 4pm.</p>
                                <p>
                                    We’ll email when your order is ready.{' '}
                                    <a href={props.context.app.config.cartLineLearnMoreLink || '#'}>Learn more</a>
                                </p>
                                {showLowStockWarning ? <p className='delivery-note'>Hurry, just a few left - order soon!</p> : <></>}
                            </div>
                        </div>
                    </label>
                </>
            );
        }
        return shippingMethods;
    };

    const _getDeliveryOption = (pickupStore: IPickUpInStoreProps, deliveryOptions: ProductDeliveryOptions) => {
        const delivery = deliveryOptions?.DeliveryOptions?.find(option => option.Code === pickupStore.pickupDeliveryModeCode);
        return delivery?.Description;
    };
    // customization- remove code to get the change store item on cart page
    /**
     * Method to check if we have atleast one common delivery code between product and channel.
     * @param productdeliveryOptions - Product DeliveryOption List.
     * @param storePickUpOptionList - Channel DeliveryOption List.
     * @returns Boolean flag.
     */
    if (
        !props.deliveryOptions ||
        !props.deliveryOptions.DeliveryOptions ||
        !props.pickupDeliveryModeCode ||
        (!props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'HD') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'STD') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'STD-2') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'PF') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'DTC-05days') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'DTC-10days') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'DTC-14days') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === 'HD Presale') &&
            !props.deliveryOptions.DeliveryOptions.find(option => option.Code === props.pickupDeliveryModeCode))
    ) {
        return undefined;
    }
    const isPFItem = props.deliveryOptions?.DeliveryOptions?.find(del => del.Code === 'PF');
    const isHomeDelAvailable = props.productAvailabilities?.find(
        pa => pa.IsProductAvailable && pa.ProductAvailableQuantity.ProductId === props.cartline.ProductId && pa.InventLocationId === 'ECOM'
    );

    return {
        callbacks: {
            toggleBopis: _toggleBOPIS
        },
        defaultComponent: _renderShippingMethod(
            props.cartline,
            props.shipitText,
            props.pickUpInStoreText,
            props.changeStoreText,
            props.orgUnitLocations,
            props.deliveryOptions,
            props.context
        ),

        orgUnitName: _getOrgUnitName(props.cartline.FulfillmentStoreId, props.orgUnitLocations),
        isBopisSelected: !!props.cartline.FulfillmentStoreId,
        deliveryOption: _getDeliveryOption(props, props.deliveryOptions),
        ContainerProps: {
            className: `msc-cart-line__bopis-container
            ${
                (isPFItem && !isPlantDeliveryStockAvailable) ||
                (!isCnCAvailable && isHomeDelAvailable) ||
                (!isHomeDelAvailable && isCnCAvailable) ||
                !isCnCAvailable?.IsProductAvailable === true
                    ? 'only-one-available'
                    : ''
            }`
        }
    };
};
